import { useMemo } from 'react';

import { useQuery } from 'urql';

import createCustomHook from 'lib/helpers/hookFactory';
import { GET_ASSET_REGIONS_NOMENCLATURE } from 'lib/queries';

import { useFeatureFlag } from '../../hooks/useFeatureFlag'; // Adjusted path
import { useAnomalyOverviewFilters } from '../useAnomalyOverviewFilters';

export interface AssetNomenclatureData {
    combinerId?: string;
    inverterId?: string;
    stackId?: string;
    positionId?: string;
}

// This is the function that will be passed to createCustomHook's callback
const ValueProvider = () => {
    const { superAnalysisTaskUUIDs, fetching: fetchingAnomalyOverview } = useAnomalyOverviewFilters();
    const isMultiModuleUpdateEnabled = useFeatureFlag('IS_MULTI_MODULE_UPDATE_ENABLED');

    const [{ data: assetRegionsNomenclatureData, fetching: fetchingNomenclature }] = useQuery({
        query: GET_ASSET_REGIONS_NOMENCLATURE,
        variables: {
            superAnalysisTaskUUIDs,
        },
        pause:
            fetchingAnomalyOverview ||
            !isMultiModuleUpdateEnabled ||
            !superAnalysisTaskUUIDs ||
            superAnalysisTaskUUIDs.length === 0,
    });

    const nomenclatureMap = useMemo(() => {
        const newMap = new Map<string, AssetNomenclatureData>();

        if (assetRegionsNomenclatureData?.aois) {
            assetRegionsNomenclatureData.aois.forEach((aoi: any) => {
                aoi.aoiAssetRegions.forEach((aoiAssetRegion: any) => {
                    const { assetRegion, assetRegionUUID } = aoiAssetRegion;

                    if (assetRegion) {
                        newMap.set(assetRegionUUID, {
                            combinerId:
                                assetRegion.combinerId ??
                                assetRegion.combinerIdBackup ??
                                assetRegion.combinerIdBackup2 ??
                                assetRegion.combinerIdBackup3,
                            inverterId:
                                assetRegion.inverterId ??
                                assetRegion.inverterIdBackup ??
                                assetRegion.inverterIdBackup2 ??
                                assetRegion.inverterIdBackup3,
                            stackId: assetRegion.stack,
                            positionId: assetRegion.positionId,
                        });
                    }
                });
            });
        }

        return newMap;
    }, [assetRegionsNomenclatureData]);

    return {
        nomenclatureMap,
        loading: fetchingNomenclature || fetchingAnomalyOverview,
    };
};

const { Provider: AssetNomenclatureProvider, useCustomHook: useAssetNomenclature } = createCustomHook<
    ReturnType<typeof ValueProvider>
>({
    name: 'useAssetNomenclature',
    callback: ValueProvider,
});

export { AssetNomenclatureProvider, useAssetNomenclature };
export type { AssetNomenclatureData as AssetNomenclature }; // Exporting the data type with its original alias
